import { getCreateDomain } from '@/lib/configs'
import { API_GET_LOGIN_STATUS, API_GET_ME_V4, API_UPDATE_ACCOUNTS_V4 } from '@/services/api'
import { fetchCMSApiWithStatusCode } from '@/services/cms'
import { LoginStatusResponse } from '@/types/auth'
import { BannerMeta, Media, SocialLink } from '@/types/store'

type MyProfileResponse = {
  id: string
  htc_account_id: string
  htc_account_email: string
  htc_account_phone_number: string
  wallet_address: string
  display_name: string
  is_admin: boolean
  liked_rooms: string[]
  liked_events: string[]
  oursong_account_id: string
  is_over_18: boolean
  active_avatar: {
    id: string
    snapshot_url: string
    avatar_url: string
    head_icon_url: string
    gender: number
    data_type: number
    is_half_body: boolean
  }
  community_user_id: string
  bio: string
  profile_banner: string
  profile_banner_meta: BannerMeta | null
  profile_image: string
  profile_media: Media[]
  show_social_links: boolean
  social_links: SocialLink[]
}

export type GetProfileMeResponse = {
  id: string
  displayName: string
  walletAddress: string
  accountEmail: string
  accountPhoneNumber: string
  isOver18: boolean
  userId: string
  activeAvatar: {
    id: string
    snapshot: string
    glbUrl: string
    dataType: number | null
    gender: number
    isHalfBody: boolean
  }
  bio: string
  profileBanner: string
  profileBannerMeta: BannerMeta | null
  profileImage: string
  profileMedia: Media[]
  showSocialLinks: boolean
  socialLinks: SocialLink[]
}

export const getProfileMe = async (htcToken: string): Promise<GetProfileMeResponse | null> => {
  const myProfileResult = (
    await fetchCMSApiWithStatusCode<MyProfileResponse>(API_GET_ME_V4, 'GET', null, {}, htcToken)
  )?.results

  if (!myProfileResult) {
    return null
  }

  const {
    id,
    bio,
    display_name: displayName = '',
    active_avatar: activeAvatar = null,
    wallet_address: walletAddress = '',
    htc_account_email: accountEmail = '',
    htc_account_phone_number: accountPhoneNumber = '',
    is_over_18: isOver18 = false,
    community_user_id: userId = '',
    profile_banner: profileBanner = '',
    profile_banner_meta: profileBannerMeta = null,
    profile_image: profileImage = '',
    profile_media: profileMedia = [],
    show_social_links: showSocialLinks = false,
    social_links: socialLinks = [],
  } = myProfileResult

  return {
    id,
    bio,
    displayName,
    walletAddress,
    accountEmail,
    accountPhoneNumber,
    isOver18,
    userId,
    activeAvatar: {
      id: activeAvatar?.id || '',
      snapshot: activeAvatar?.head_icon_url || activeAvatar?.snapshot_url || '',
      glbUrl: activeAvatar?.avatar_url || '', // 3D model of the avatar
      dataType: activeAvatar?.data_type || null, // Data type from vive avatar
      gender: activeAvatar?.gender || 0, // 0 for female and 1 for male
      isHalfBody: activeAvatar?.is_half_body || false,
    },
    profileImage,
    profileBanner,
    profileBannerMeta,
    profileMedia: profileMedia.map((media) => ({
      ...(media.image && { image: media.image }),
      ...(media.video && { video: media.video }),
    })),
    showSocialLinks,
    socialLinks,
  }
}

export const updateProfileMeByCmsAccountId = (
  htcToken: string,
  cmsAccountId: string,
  profile: Partial<MyProfileResponse>,
) => {
  return fetchCMSApiWithStatusCode<MyProfileResponse>(
    `${API_UPDATE_ACCOUNTS_V4}/${cmsAccountId}`,
    'PATCH',
    // @ts-ignore
    profile,
    {},
    htcToken,
  )
}

export const getLoginStatus = async (): Promise<LoginStatusResponse> => {
  const createDomain = getCreateDomain()
  const url = `https://${createDomain}${API_GET_LOGIN_STATUS}`
  try {
    const res = await fetch(url, {
      method: 'GET',
      // 🥷🏼 for worlds domain, will need this line
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    })

    if (!res.ok || res.status === 204) return { status: res.status, data: null }

    const data = await res.json()
    return { status: res.status, data }
  } catch (error) {
    console.error('getLoginStatus network error:', error)
    return { status: 500, data: null }
  }
}
